import { useState } from "react";
import type { GalleryItem } from "../../../lib/api/socialApi";
import { Button } from "../../../components/ui/Button";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";

/**
 * FS-03-03 친구 기계 데려오기(로컬) — SCR v0.6 sfs-03 오른쪽 창.
 * 구성: 선택 목록(공개 작품 — 서버가 공개 범위를 거른 전시관 목록, 내 작품 제외) · 배치 슬롯 5 · 성능 게이지(열화 표시).
 * 조작: 추가/제거 · 시작('같이 움직이기'). 상태: 0대 · 1~5대 · 성능 열화 중.
 * 친구 기계는 자기 프로그램대로 움직이고 리모컨은 내 기계만(SR-LCL-02).
 */
export function FriendsPanel({
  items,
  slots,
  max,
  playing,
  hasMachine,
  degraded,
  gauge,
  onAdd,
  onRemove,
  onStart,
  onStop,
}: {
  /** 공개 작품(내 작품 제외 · 서버 순서) */
  items: readonly GalleryItem[];
  slots: readonly string[];
  max: number;
  playing: boolean;
  hasMachine: boolean;
  degraded: boolean;
  gauge: number;
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
  onStart: () => void;
  onStop: () => void;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const se = density === "se";
  const [listOpen, setListOpen] = useState(false);
  const byId = new Map(items.map((i) => [i.snapshotId, i]));
  const free = Math.max(0, max - slots.length);
  const candidates = items.filter((i) => !slots.includes(i.snapshotId) && !i.static);
  const quick = candidates.slice(0, 2);

  return (
    <aside
      className={`pf-pane pf-gallery__side pf-friends pf-friends--${density}`}
      aria-labelledby="friends-title"
      data-testid="fs-03-03-panel"
      data-count={slots.length}
      data-degraded={degraded}
    >
      <h2 id="friends-title" className="pf-h2">
        {t("gallery.friends.title")}
      </h2>
      <p className="pf-muted">{t("gallery.friends.sub", { max })}</p>
      <ul className="pf-friends__slots" aria-label={t("gallery.friends.slots")}>
        <li className="pf-friends__slot pf-friends__slot--me" data-testid="slot-me">
          <b>{t("gallery.label.mine")}</b>
          {!hasMachine && <span className="pf-muted">{t("gallery.friends.noMachine")}</span>}
        </li>
        {slots.map((id) => (
          <li key={id} className="pf-friends__slot" data-testid="slot-friend" data-owner={id}>
            <b>{byId.get(id)?.nickname ?? ""}</b>
            <Button
              variant="ghost"
              className="pf-btn--sm pf-friends__x"
              aria-label={t("gallery.friends.remove", { name: byId.get(id)?.nickname ?? "" })}
              disabled={playing}
              onClick={() => onRemove(id)}
            >
              ✕
            </Button>
          </li>
        ))}
      </ul>
      {!playing && (
        <div className="pf-pane pf-pane--dim pf-friends__free" data-testid="slot-free">
          <span className="pf-muted">
            {free > 0 ? t("gallery.friends.free", { n: free }) : t("gallery.friends.full", { max })}
          </span>
          {free > 0 &&
            quick.map((i) => (
              <Button
                key={i.snapshotId}
                variant="ghost"
                className="pf-btn--sm"
                onClick={() => onAdd(i.snapshotId)}
                data-testid="friend-quick-add"
                aria-label={t("gallery.friends.addName", { name: i.nickname })}
              >
                {i.nickname} ＋
              </Button>
            ))}
          {free > 0 && candidates.length > quick.length && (
            <Button
              variant="ghost"
              className="pf-btn--sm"
              aria-expanded={listOpen}
              aria-controls="friends-list"
              onClick={() => setListOpen((o) => !o)}
              data-testid="friend-list-open"
            >
              {t("gallery.friends.more")}
            </Button>
          )}
        </div>
      )}
      {listOpen && !playing && (
        <ul
          id="friends-list"
          className="pf-friends__list"
          aria-label={t("gallery.friends.list")}
          data-testid="friend-list"
        >
          {candidates.map((i) => (
            <li key={i.snapshotId}>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                disabled={free === 0}
                onClick={() => onAdd(i.snapshotId)}
                data-owner={i.snapshotId}
              >
                {i.nickname} ＋
              </Button>
            </li>
          ))}
        </ul>
      )}
      <div className="pf-friends__gauge" data-testid="friends-gauge">
        <span className="pf-muted">{t("gallery.friends.power")}</span>
        <div
          className={`pf-progress${degraded ? " pf-progress--warn" : ""}`}
          role="meter"
          aria-label={t("gallery.friends.power")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(gauge * 100)}
        >
          <span className="pf-progress__fill" style={{ width: `${Math.round(gauge * 100)}%` }} />
        </div>
        <span className="pf-muted" data-testid="friends-degrade" role="status">
          {degraded ? t("gallery.friends.degraded") : t("gallery.friends.auto")}
        </span>
      </div>
      <div className="pf-friends__end">
        {playing ? (
          <Button
            variant="ghost"
            block
            icon={se ? "🏛" : undefined}
            onClick={onStop}
            data-testid="friends-stop"
          >
            {t("gallery.friends.stop")}
          </Button>
        ) : (
          <Button
            variant="green"
            block
            icon={se ? "▶" : undefined}
            disabled={!hasMachine}
            onClick={onStart}
            data-testid="friends-start"
          >
            {t("gallery.friends.start")}
          </Button>
        )}
      </div>
    </aside>
  );
}
